import { useEffect, useState } from 'react';

/**
 * The browser fires `beforeinstallprompt` once per session when the page
 * meets installability criteria. We stash the event so we can call
 * `prompt()` later from a click handler — calling it cold throws.
 */
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

const VISIT_KEY = 'margin:pwa-visits';
const DISMISSED_KEY = 'margin:pwa-install-dismissed';
const VISIT_THRESHOLD = 2;

function readNumber(key: string): number {
  if (typeof localStorage === 'undefined') return 0;
  const raw = localStorage.getItem(key);
  if (!raw) return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function readFlag(key: string): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(key) === '1';
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  // iOS Safari sets navigator.standalone when launched from home screen.
  const nav = window.navigator as unknown as { standalone?: boolean };
  return nav.standalone === true;
}

export type PwaInstallState = {
  /** True when the deferred prompt is available AND the user is eligible. */
  canShow: boolean;
  /** True after the user installs (success path) or explicitly dismisses. */
  hidden: boolean;
  /** Trigger the native install prompt. Resolves when the user picks. */
  promptInstall: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
  /** Hide the banner without showing the native prompt. Persists. */
  dismiss: () => void;
};

/**
 * Hook that:
 *   - Bumps visit count once per mount.
 *   - Captures the deferred beforeinstallprompt event.
 *   - Exposes whether the banner should show now (visits >= 2,
 *     not already installed, not previously dismissed).
 *   - Provides promptInstall() + dismiss() handlers.
 */
export function usePwaInstall(): PwaInstallState {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(
    null,
  );
  const [hidden, setHidden] = useState(false);
  const [visits, setVisits] = useState<number>(() => readNumber(VISIT_KEY));
  const [dismissed, setDismissed] = useState<boolean>(() =>
    readFlag(DISMISSED_KEY),
  );

  // Bump visit count once per browser tab/session.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const next = readNumber(VISIT_KEY) + 1;
    try {
      localStorage.setItem(VISIT_KEY, String(next));
    } catch {
      /* ignore quota / private mode */
    }
    setVisits(next);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPrompt = (e: Event) => {
      e.preventDefault(); // we'll show our own banner
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setDeferred(null);
      setHidden(true);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const canShow =
    !hidden &&
    !dismissed &&
    !isStandalone() &&
    visits >= VISIT_THRESHOLD &&
    !!deferred;

  const promptInstall = async () => {
    if (!deferred) return 'unavailable' as const;
    await deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    setHidden(true);
    if (choice.outcome === 'dismissed') {
      // Treat a dismissal of the native prompt as a soft dismiss too.
      try {
        localStorage.setItem(DISMISSED_KEY, '1');
      } catch {
        /* ignore */
      }
      setDismissed(true);
    }
    return choice.outcome;
  };

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      /* ignore */
    }
    setDismissed(true);
    setHidden(true);
  };

  return { canShow, hidden, promptInstall, dismiss };
}
